// Independent native-video comparison. No WebGL renderer or old scene is loaded.
if (!window.__introLabRedirecting) initializeVideoLab();

function initializeVideoLab() {
  const $ = selector => document.querySelector(selector);
  const stage = $('#cinema');
  const video = $('#film-video');
  const playButton = $('#play');
  const replayButton = $('#replay');
  const timeline = $('#timeline');
  const fullscreenTimeline = $('#fs-timeline');
  const soundButton = $('#sound');
  const centerPlay = $('#center-play');
  const fallback = $('#fallback');
  const fallbackPoster = $('#fallback-poster');
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const films = {
    e1: {title: '鑿光成家', description: '石材的厚度、木質的溫度，隨天光慢慢展開。'},
    e2: {title: '光隙入境', description: '穿過重重光隙，在明暗之間找到家的輪廓。'}
  };
  const requested = new URL(location.href).searchParams.get('film')?.toLowerCase();
  let film = Object.hasOwn(films, requested) ? requested : 'e1';
  let portrait = false;
  let ready = false;
  let playing = false;
  let elapsed = 0;
  let duration = 8;
  let generation = 0;
  let activeController;
  let pendingResolve;
  let loadingPromise = Promise.resolve();
  let frameCallback = 0;
  let animationFrame = 0;
  let resizeFrame = 0;
  let scrubbing = false;
  let rememberedFocus;
  let fallbackScroll = 0;
  let activeObjectURL;
  const clamp = value => Math.max(0, Math.min(duration, Number(value) || 0));
  const asset = (name, ending) => `/assets/round3/films/${name}-${portrait ? 'mobile' : 'desktop'}${ending}`;
  const isStatic = () => reduced.matches || stage.dataset.state === 'static' || stage.dataset.state === 'error';
  const measurePortrait = () => {
    const bounds = stage.getBoundingClientRect();
    return bounds.width / Math.max(1, bounds.height) < .8;
  };

  function quality() {
    const data = video.getVideoPlaybackQuality?.();
    return {totalVideoFrames: data?.totalVideoFrames ?? video.webkitDecodedFrameCount ?? 0,
      droppedVideoFrames: data?.droppedVideoFrames ?? video.webkitDroppedFrameCount ?? 0};
  }

  function snapshot() {
    return {film, ready, playing: playing && !video.paused, time: elapsed, duration, portrait,
      mode: 'video', quality: quality(), videoCount: document.querySelectorAll('video').length,
      videoWidth: video.videoWidth, videoHeight: video.videoHeight};
  }

  function announce(message) { $('#playback-status').textContent = message; }

  function metadata() {
    const selected = films[film];
    document.title = `${film.toUpperCase()} ${selected.title} — 宏稷設計`;
    document.querySelectorAll('[data-film]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.film === film)));
    $('#film-title').textContent = selected.title;
    $('#film-description').textContent = selected.description;
    $('#note-number').textContent = `${film.toUpperCase()} / LAB 03`;
    stage.setAttribute('aria-label', `${film.toUpperCase()} ${selected.title} 影片播放器`);
    video.setAttribute('aria-label', `${selected.title}開場影像`);
    centerPlay.setAttribute('aria-label', `播放${selected.title}`);
    const url = new URL(location.href);
    url.searchParams.set('film', film);
    history.replaceState({}, '', url);
    $('#share').href = url.href;
    $('#share').innerHTML = `單獨觀看 ${film.toUpperCase()} <span aria-hidden="true">↗</span>`;
    stage.dataset.film = film;
    stage.dataset.orientation = portrait ? 'portrait' : 'landscape';
  }

  function updateTransport() {
    const mediaEnabled = ready && !isStatic();
    playButton.disabled = replayButton.disabled = timeline.disabled = soundButton.disabled = !mediaEnabled;
    $('#fs-play').disabled = $('#fs-replay').disabled = fullscreenTimeline.disabled = !mediaEnabled;
    $('#fs-sound').disabled = !mediaEnabled;
    playButton.querySelector('.play-icon').textContent = playing ? 'Ⅱ' : '▷';
    playButton.querySelector('.play-label').textContent = playing ? '暫停' : '播放';
    playButton.setAttribute('aria-label', playing ? '暫停' : elapsed >= duration - .03 ? '從頭播放' : '播放');
    $('#fs-play').textContent = playing ? '暫停' : '播放';
    $('#time').textContent = `${elapsed.toFixed(1).padStart(4, '0')} / ${duration.toFixed(1).padStart(4, '0')}`;
    for (const input of [timeline, fullscreenTimeline]) {
      input.max = duration;
      if (!scrubbing) input.value = elapsed;
      input.setAttribute('aria-valuetext', `${elapsed.toFixed(1)} 秒，共 ${duration.toFixed(1)} 秒`);
    }
    centerPlay.hidden = playing || !mediaEnabled || elapsed >= duration - .03;
    soundButton.textContent = video.muted ? '聲音：關' : '聲音：開';
    soundButton.setAttribute('aria-pressed', String(!video.muted));
    soundButton.setAttribute('aria-label', video.muted ? '開啟聲音' : '關閉聲音');
    $('#fs-sound').textContent = soundButton.textContent;
    $('#fs-sound').setAttribute('aria-pressed', String(!video.muted));
    $('#fs-sound').setAttribute('aria-label', video.muted ? '開啟聲音' : '關閉聲音');
    stage.dataset.playing = String(playing);
    stage.dataset.time = elapsed.toFixed(3);
    stage.dataset.ready = String(ready);
    stage.dataset.completed = String(elapsed >= duration - .03);
  }

  function stopFrames() {
    if (frameCallback) video.cancelVideoFrameCallback?.(frameCallback);
    if (animationFrame) cancelAnimationFrame(animationFrame);
    frameCallback = animationFrame = 0;
  }

  function followFrames(token) {
    stopFrames();
    const next = () => {
      if (token !== generation || video.paused || video.ended || document.hidden) return;
      elapsed = clamp(video.currentTime);
      updateTransport();
      if (video.requestVideoFrameCallback) frameCallback = video.requestVideoFrameCallback(next);
      else animationFrame = requestAnimationFrame(next);
    };
    if (video.requestVideoFrameCallback) frameCallback = video.requestVideoFrameCallback(next);
    else animationFrame = requestAnimationFrame(next);
  }

  function pause() {
    video.pause();
    playing = false;
    stopFrames();
    if (!isStatic() && video.readyState >= 1) elapsed = clamp(video.currentTime);
    updateTransport();
    return snapshot();
  }

  function cancelDecode() {
    stopFrames();
    video.pause();
    video.removeAttribute('src');
    video.load();
    if (activeObjectURL) { URL.revokeObjectURL(activeObjectURL); activeObjectURL = undefined; }
  }

  function showStill(message, error = false) {
    playing = false;
    ready = !error;
    stage.dataset.state = error ? 'error' : 'static';
    if (!error) elapsed = duration;
    fallback.hidden = false;
    fallback.classList.remove('brand-only');
    fallbackPoster.src = asset(film, '-end.jpg');
    $('#fallback-message').textContent = message;
    announce(message);
    updateTransport();
  }

  fallbackPoster.addEventListener('error', () => {
    if (!fallbackPoster.src.endsWith('/assets/logo-original.jpg')) {
      fallback.classList.add('brand-only');
      fallbackPoster.src = '/assets/logo-original.jpg';
    }
  });

  function loadFilm(nextFilm, {time = 0, resume = false, force = false} = {}) {
    if (!Object.hasOwn(films, nextFilm)) return Promise.reject(new RangeError('Unknown film'));
    if (!force && nextFilm === film && ready) return Promise.resolve(snapshot());
    if (!force && nextFilm === film && stage.dataset.state === 'loading' && activeController) return loadingPromise;
    const token = ++generation;
    activeController?.abort();
    pendingResolve?.({cancelled: true});
    pendingResolve = undefined;
    cancelDecode();
    const controller = new AbortController();
    activeController = controller;
    film = nextFilm;
    portrait = measurePortrait();
    elapsed = Math.max(0, Number(time) || 0);
    duration = 8;
    ready = playing = false;
    fallback.hidden = true;
    stage.dataset.state = 'loading';
    metadata();
    video.poster = asset(film, elapsed >= duration - .03 ? '-end.jpg' : '-start.jpg');
    video.volume = 1;
    video.preload = 'auto';
    updateTransport();
    if (reduced.matches) {
      video.preload = 'none';
      showStill('已依減少動態效果設定，呈現光與空間的定格。');
      return loadingPromise = Promise.resolve(snapshot());
    }
    announce('影片準備中。');
    $('#player-status').textContent = '影片準備中…';
    const downloadURL = new URL(asset(film, '.mp4'), location.href).href;
    let expected;
    const current = () => token === generation && !controller.signal.aborted && !reduced.matches && video.currentSrc === expected;
    let targetTime = elapsed;
    let restored = targetTime <= .001;
    let canPlay = false;
    let settled = false;
    let timeout;
    loadingPromise = new Promise(resolve => { pendingResolve = resolve; });
    const settle = () => {
      if (settled) return;
      settled = true;
      clearTimeout(timeout);
      const resolve = pendingResolve;
      pendingResolve = undefined;
      resolve?.(snapshot());
    };
    const fail = () => {
      if (!current() || !video.error) return;
      cancelDecode();
      showStill('影片暫時無法播放，先看光的定格。', true);
      settle();
    };
    const complete = () => {
      if (!current() || !restored || video.seeking || ready) return;
      const atEnd = targetTime >= duration - .03 && video.readyState >= 2;
      if (!canPlay && !atEnd) return;
      ready = true;
      elapsed = clamp(video.currentTime);
      stage.dataset.state = 'ready';
      announce(elapsed >= duration - .03 ? '影像已定格。' : '已準備好，按下播放。');
      updateTransport();
      settle();
      if (resume && !document.hidden) void start(false);
    };
    const listen = (event, callback) => video.addEventListener(event, callback, {signal: controller.signal});
    listen('loadedmetadata', () => {
      if (!current()) return;
      duration = Number.isFinite(video.duration) && video.duration > 0 ? video.duration : 8;
      targetTime = clamp(targetTime);
      if (targetTime > .001) {
        restored = false;
        try { video.currentTime = targetTime; } catch { restored = true; }
      } else restored = true;
      updateTransport();
    });
    listen('canplay', () => { if (current() && video.readyState >= 3) { canPlay = true; complete(); } });
    listen('loadeddata', complete);
    listen('seeked', () => { if (current()) { restored = true; elapsed = clamp(video.currentTime); complete(); updateTransport(); } });
    listen('playing', () => {
      if (!current()) return;
      playing = true;
      stage.dataset.state = 'ready';
      announce('正在播放。');
      updateTransport();
      followFrames(token);
    });
    listen('pause', () => {
      if (!current() || !video.paused) return;
      playing = false;
      stopFrames();
      if (video.readyState >= 1) elapsed = clamp(video.currentTime);
      updateTransport();
    });
    listen('timeupdate', () => { if (current() && ready) { elapsed = clamp(video.currentTime); updateTransport(); } });
    listen('waiting', () => { if (current() && ready && !video.paused) announce('正在接續影像…'); });
    listen('ended', () => {
      if (!current()) return;
      playing = false;
      elapsed = duration;
      stopFrames();
      announce('影像已定格，可重播或觀看另一段旅程。');
      updateTransport();
      // Keep the video's decoded final frame. Never replace it with a DOM endcard.
    });
    listen('error', fail);
    controller.signal.addEventListener('abort', () => clearTimeout(timeout), {once: true});
    timeout = setTimeout(() => {
      if (token !== generation || ready || controller.signal.aborted) return;
      controller.abort();
      cancelDecode();
      showStill('影片仍在路上，先看光的定格。重新選擇影像即可再試。', true);
      settle();
    }, 45000);
    // These short films are loaded completely before playback. A local Blob
    // gives native seeking on hosts without HTTP 206 and prevents mid-film IO.
    void (async () => {
      try {
        const response = await fetch(downloadURL, {signal: controller.signal});
        if (!response.ok) throw new Error('Film download failed');
        let blob = await response.blob();
        if (token !== generation || controller.signal.aborted || reduced.matches) return;
        if (!blob.type.startsWith('video/')) blob = new Blob([blob], {type: 'video/mp4'});
        activeObjectURL = expected = URL.createObjectURL(blob);
        video.src = expected;
        video.load();
      } catch {
        if (token !== generation || controller.signal.aborted) return;
        cancelDecode();
        showStill('影片暫時無法準備，先看光的定格。重新選擇影像即可再試。', true);
        settle();
      }
    })();
    return loadingPromise;
  }

  function fitCinema() {
    if (document.fullscreenElement || stage.classList.contains('is-fullscreen')) return;
    const bounds = stage.getBoundingClientRect();
    if (bounds.top < 12 || bounds.bottom > innerHeight - 12) stage.scrollIntoView({block: portrait || bounds.height > innerHeight - 24 ? 'start' : 'center', behavior: reduced.matches ? 'auto' : 'smooth'});
  }

  async function start(fit = true) {
    const token = generation;
    if (!ready) await loadingPromise;
    if (token !== generation || !ready || isStatic()) return snapshot();
    if (fit) fitCinema();
    if (video.ended || elapsed >= duration - .03) video.currentTime = 0;
    try {
      await video.play();
      if (token !== generation) return snapshot();
      playing = !video.paused;
      updateTransport();
    } catch {
      if (token === generation) {
        playing = false;
        announce('按下播放，繼續這段光的旅程。');
        updateTransport();
      }
    }
    return snapshot();
  }

  async function seek(value) {
    const token = generation;
    if (!ready) await loadingPromise;
    if (token !== generation || !ready || isStatic()) return snapshot();
    pause();
    const target = clamp(value);
    if (Math.abs(video.currentTime - target) < .002 && !video.seeking) { elapsed = target; updateTransport(); return snapshot(); }
    return new Promise((resolve, reject) => {
      let timer;
      const signal = activeController?.signal;
      let finished = false;
      const clean = () => {
        clearTimeout(timer);
        video.removeEventListener('seeked', onSeeked);
        video.removeEventListener('error', failed);
        signal?.removeEventListener('abort', cancelled);
      };
      const finish = () => {
        if (finished) return;
        finished = true;
        clean();
        if (token === generation) { elapsed = clamp(video.currentTime); updateTransport(); }
        resolve(snapshot());
      };
      const failed = () => {
        if (finished) return;
        finished = true;
        clean();
        reject(new Error('Video seek did not complete'));
      };
      const cancelled = () => {
        if (finished) return;
        finished = true;
        clean();
        resolve({...snapshot(), cancelled: true});
      };
      const onSeeked = () => { if (!video.seeking) finish(); };
      video.addEventListener('seeked', onSeeked);
      video.addEventListener('error', failed);
      signal?.addEventListener('abort', cancelled, {once: true});
      timer = setTimeout(failed, 10000);
      try { video.currentTime = target; } catch { failed(); }
    });
  }

  async function replay() {
    if (!ready || isStatic()) return snapshot();
    pause();
    video.currentTime = 0;
    elapsed = 0;
    updateTransport();
    return start();
  }

  function syncFullscreen() {
    const enabled = document.fullscreenElement === stage || stage.classList.contains('is-fullscreen');
    $('#fullscreen').setAttribute('aria-label', enabled ? '退出全螢幕' : '全螢幕觀看');
    $('#fullscreen').setAttribute('aria-pressed', String(enabled));
  }

  async function exitFullscreen() {
    if (document.fullscreenElement) { try { await document.exitFullscreen(); } catch { /* CSS fallback remains available. */ } }
    if (stage.classList.contains('is-fullscreen')) {
      stage.classList.remove('is-fullscreen');
      document.body.classList.remove('fullscreen-fallback');
      window.scrollTo({top: fallbackScroll, behavior: 'auto'});
    }
    syncFullscreen();
    rememberedFocus?.focus({preventScroll: true});
  }

  async function toggleFullscreen() {
    if (document.fullscreenElement || stage.classList.contains('is-fullscreen')) return exitFullscreen();
    rememberedFocus = document.activeElement;
    if (stage.requestFullscreen) {
      try { await stage.requestFullscreen(); syncFullscreen(); $('#fs-close').focus({preventScroll: true}); return; } catch { /* iOS and restricted embeds use the same visible controls below. */ }
    }
    fallbackScroll = window.scrollY;
    stage.classList.add('is-fullscreen');
    document.body.classList.add('fullscreen-fallback');
    syncFullscreen();
    $('#fs-close').focus({preventScroll: true});
  }

  playButton.addEventListener('click', () => playing ? pause() : void start());
  centerPlay.addEventListener('click', () => void start());
  replayButton.addEventListener('click', () => void replay());
  $('#fs-play').addEventListener('click', () => playing ? pause() : void start(false));
  $('#fs-replay').addEventListener('click', () => void replay());
  $('#fullscreen').addEventListener('click', () => void toggleFullscreen());
  $('#fs-close').addEventListener('click', () => void exitFullscreen());
  soundButton.addEventListener('click', () => { video.volume = 1; video.muted = !video.muted; updateTransport(); });
  $('#fs-sound').addEventListener('click', () => { video.volume = 1; video.muted = !video.muted; updateTransport(); });
  for (const input of [timeline, fullscreenTimeline]) {
    input.addEventListener('pointerdown', () => { scrubbing = true; pause(); });
    input.addEventListener('input', () => { const target = clamp(input.value); pause(); elapsed = target; if (ready && !isStatic()) video.currentTime = target; updateTransport(); });
    input.addEventListener('change', () => { const target = input.value; scrubbing = false; void seek(target).catch(() => announce('此刻影像暫時無法定位，請重新選擇影片。')); });
    input.addEventListener('pointerup', () => { scrubbing = false; updateTransport(); });
    input.addEventListener('pointercancel', () => { scrubbing = false; updateTransport(); });
  }
  document.querySelectorAll('[data-film]').forEach(button => button.addEventListener('click', () => void loadFilm(button.dataset.film)));
  document.addEventListener('fullscreenchange', syncFullscreen);
  document.addEventListener('keydown', event => { if (event.key === 'Escape' && stage.classList.contains('is-fullscreen')) void exitFullscreen(); });
  document.addEventListener('visibilitychange', () => { if (document.hidden) pause(); });
  const observer = new ResizeObserver(() => {
    cancelAnimationFrame(resizeFrame);
    resizeFrame = requestAnimationFrame(() => {
      const next = measurePortrait();
      if (next === portrait) return;
      const preservedTime = ready && !isStatic() ? video.currentTime : elapsed;
      const wasPlaying = playing && !video.paused;
      void loadFilm(film, {time: preservedTime, resume: wasPlaying, force: true});
    });
  });
  observer.observe(stage);
  const preferenceChanged = () => void loadFilm(film, {force: true});
  if (reduced.addEventListener) reduced.addEventListener('change', preferenceChanged);
  else reduced.addListener(preferenceChanged);
  window.addEventListener('pagehide', () => { pause(); activeController?.abort(); pendingResolve?.({cancelled: true}); pendingResolve = undefined; cancelDecode(); });
  window.addEventListener('pageshow', event => { if (event.persisted) void loadFilm(film, {time: elapsed, force: true}); });
  window.__introLab = {get state() { return snapshot(); }, inspect: snapshot, seek,
    async select(value) { return loadFilm(String(value).toLowerCase()); }, play: () => start(), pause};
  void loadFilm(film, {force: true});
}
